import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Label, TextInput, Select, Datepicker, Badge, Modal } from 'flowbite-react';
import { FiArrowLeft, FiDatabase, FiCheck, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import * as sched from 'sched';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Formik, Form, Field } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { getWorkspaceCredentials, getDecryptedCredentialValue, StoredCredential, getProviderToken } from '../../utils/credentialStorage';
import { useSnackbar } from 'notistack';

// Define the import request form values
interface ImportFormValues {
  limit: string;
  updatedAfter: Date | null;
  includePeople: boolean;
  includeProjects: boolean;
  includeWorkItems: boolean;
  followOutOfScopeLinks: boolean;
}

const DataSourceImportPage: React.FC = () => {
  const { workspaceId, dataSourceId } = useParams<{ workspaceId: string; dataSourceId: string }>();
  const { client, token } = useAuth();
  const { safeData } = useContext(WorkspaceContext);
  const navigate = useNavigate();

  // State for data source and import results
  const [dataSource, setDataSource] = useState<sched.DataSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<sched.ImportActionCommandAndDescription[] | null>(null);
  // Track which rows are selected for import
  const [selectedRows, setSelectedRows] = useState<boolean[]>([]);
  // Detail modal state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailJson, setDetailJson] = useState<string>('');
  // Sorting state for results table
  const [sortBy, setSortBy] = useState<'type' | 'desc'>('type');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { enqueueSnackbar } = useSnackbar();

  // State for credentials
  const [credentials, setCredentials] = useState<StoredCredential[]>([]);
  const [selectedCredentialId, setSelectedCredentialId] = useState<string>('');
  const [credentialLoading, setCredentialLoading] = useState(true);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  // Load data source
  useEffect(() => {
    const loadDataSource = async () => {
      if (!workspaceId || !dataSourceId || !token || !client) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch data sources
        const sources: sched.ListDataSourcesResponse = await sched.WrappedApiClient.list_workspace_data_sources(
          client,
          workspaceId,
          token
        );

        const source = sources.data_sources.find(s => s.id === dataSourceId);
        if (!source) {
          setError(`Data source with ID ${dataSourceId} not found`);
          return;
        }

        setDataSource(source);
      } catch (err) {
        setError(`Failed to load data source: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    };

    loadDataSource();
  }, [workspaceId, dataSourceId, token]);

  // Load credentials
  useEffect(() => {
    const loadCredentials = async () => {
      if (!workspaceId) return;

      try {
        setCredentialLoading(true);
        setCredentialError(null);

        const workspaceCredentials = await getWorkspaceCredentials(workspaceId);
        setCredentials(workspaceCredentials);

        // Set the first credential as selected by default if available
        if (workspaceCredentials.length > 0) {
          setSelectedCredentialId(workspaceCredentials[0].id);
        }
      } catch (err) {
        console.error('Failed to load credentials:', err);
        setCredentialError(`Failed to load credentials: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setCredentialLoading(false);
      }
    };

    loadCredentials();
  }, [workspaceId]);

  // Calculate default date (1 week ago)
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  // Import validation schema
  const importValidationSchema = toFormikValidationSchema(
    z.object({
      limit: z.string().optional().refine(val => {
        if (!val) return true; // Empty is valid
        const num = parseInt(val, 10);
        return !isNaN(num) && num > 0;
      }, {
        message: "Limit must be a positive number or empty",
      }),
      updatedAfter: z.date().nullable(),
      includePeople: z.boolean(),
      includeProjects: z.boolean(),
      includeWorkItems: z.boolean(),
      followOutOfScopeLinks: z.boolean(),
    }).refine(data => {
      return data.includePeople || data.includeProjects || data.includeWorkItems;
    }, {
      message: "At least one data type must be selected",
      path: ["includePeople"]
    })
  );

  // Initial values for form
  const initialImportValues: ImportFormValues = {
    limit: '',
    updatedAfter: oneWeekAgo,
    includePeople: true,
    includeProjects: true,
    includeWorkItems: true,
    followOutOfScopeLinks: true,
  };

  // Initialize selection whenever new import results arrive
  useEffect(() => {
    if (importResults && importResults.length > 0) {
      // Default to all selected
      setSelectedRows(new Array(importResults.length).fill(true));
    } else {
      setSelectedRows([]);
    }
  }, [importResults]);

  const allSelected = selectedRows.length > 0 && selectedRows.every(Boolean);
  const someSelected = selectedRows.some(Boolean) && !allSelected;
  const selectedCount = selectedRows.filter(Boolean).length;

  const toggleAll = (checked: boolean) => {
    setSelectedRows(selectedRows.map(() => checked));
  };

  const toggleRow = (index: number) => {
    setSelectedRows((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const openDetail = (index: number) => {
    if (!importResults) return;
    const cmd = importResults[index]?.command;
    try {
      const json = JSON.stringify(cmd, null, 2);
      setDetailJson(json);
    } catch (e) {
      setDetailJson('Unable to stringify command');
    }
    setDetailOpen(true);
  };

  // Parse a description title of form "Create Person: Jane" into ["Create Person", "Jane"]
  const parseTitle = (title: string): { kind: string; desc: string } => {
    const idx = title.indexOf(':');
    if (idx === -1) return { kind: title, desc: '' };
    return { kind: title.slice(0, idx).trim(), desc: title.slice(idx + 1).trim() };
  };

  const toggleSort = (column: 'type' | 'desc') => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  // Handle accepting the import and applying the commands
  const handleAcceptImport = async () => {
    if (!workspaceId || !importResults || !safeData) {
      setError("Missing required data for accepting import");
      return;
    }

    try {
      setError(null);

      // Extract the commands from the selected rows only
      const selectedIndices: number[] = [];
      const commands = importResults
        .filter((_, idx) => {
          const include = selectedRows[idx];
          if (include) selectedIndices.push(idx);
          return include;
        })
        .map(result => result.command);

      // Apply the commands using the WASM function
      await sched.DataManager.apply_commands(safeData, commands);

      // Remove imported items and clear selection on remaining
      const remaining = importResults.filter((_, idx) => !selectedIndices.includes(idx));
      setImportResults(remaining.length > 0 ? remaining : null);
      setSelectedRows(remaining.map(() => false));
      enqueueSnackbar("Import Complete", { variant: "success" });

    } catch (err) {
      console.error('Error accepting import:', err);
      enqueueSnackbar(`Failed to accept import: ${err instanceof Error ? err.message : String(err)}`, { variant: "error" });
      setError(`Failed to accept import: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Handle webhook installation for automatic sync
  const handleInstallWebhook = async () => {
    if (!workspaceId || !dataSource || !selectedCredentialId) {
      enqueueSnackbar("Missing required data for webhook installation", { variant: "error" });
      return;
    }

    try {
      setWebhookLoading(true);
      setError(null);

      // Get the credential token
      const token = await getProviderToken(workspaceId, selectedCredentialId);
      if (!token) {
        throw new Error('Selected credential not found');
      }

      // Extract GitLab project or group ID based on the data source configuration
      if (dataSource.configuration.type !== 'GitLabProject' && dataSource.configuration.type !== 'GitLabGroup') {
        throw new Error('Automatic sync is only supported for GitLab projects and groups');
      }

      let id = dataSource.configuration.full_path;

      // Determine if it's a project or group
      const targetType = dataSource.configuration.type === 'GitLabProject' ? 'project' : 'group';

      // Use the workspace ID as the webhook secret
      const webhookSecret = workspaceId;

      const result = await sched.install_gitlab_webhook(token, workspaceId, webhookSecret, targetType, id, process.env.API_URL!);

      if (result) {
        enqueueSnackbar("Automatic sync enabled successfully", { variant: "success" });
      } else {
        enqueueSnackbar("Automatic sync was already enabled", { variant: "info" });
      }
    } catch (err) {
      console.error('Error installing webhook:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to enable automatic sync: ${errorMessage}`);
      enqueueSnackbar(`Failed to enable automatic sync: ${errorMessage}`, { variant: "error" });
    } finally {
      setWebhookLoading(false);
    }
  };

  // Handle import submission
  const handleImport = async (values: ImportFormValues) => {
    if (!workspaceId || !dataSourceId || !client || !safeData || !dataSource) {
      setError("Missing required data for import");
      return;
    }

    try {
      setImportLoading(true);
      setError(null);
      setImportResults(null);

      let tokenToUse: sched.ProviderToken | null = null;

      // Get the token to use (either from selected credential or fallback to auth token)
      if (selectedCredentialId) {
        try {
          const credentialToken = await getDecryptedCredentialValue(workspaceId, selectedCredentialId);
          if (credentialToken) {
            if (credentialToken.type === 'pat') {
              tokenToUse = { type: 'PAT', value: credentialToken.value }
            } else if (credentialToken.type === 'bearer_token') {
              // GitLab for whatever reason takes the ID token in the request header
              // TODO make this configurable/custom to provider
              tokenToUse = { type: 'JWTAccessToken', value: credentialToken.access_token }
            } else {
              throw new Error('Invalid credential type');
            }
          } else {
            console.warn('Selected credential not found, falling back to auth token');
          }
        } catch (credErr) {
          console.warn('Error getting credential value, falling back to auth token:', credErr);
        }
      }

      if (!tokenToUse) {
        setError("No valid token available for import");
        setImportLoading(false);
        return;
      }

      // Build import options based on the form values
      const scopes: sched.ImportOptionScope[] = [];

      if (values.includePeople) {
        scopes.push({ type: 'People' });
      }

      if (values.includeProjects) {
        scopes.push({ type: 'Projects' });
      }

      if (values.includeWorkItems) {
        scopes.push({ type: 'WorkItems' });
      }

      // Create the import options object
      const importOptions: sched.ImportOptions = {
        scopes: scopes,
        limit: values.limit ? parseInt(values.limit, 10) : null,
        updated_after: values.updatedAfter ? values.updatedAfter.toISOString() : null,
        follow_out_of_scope_links: values.followOutOfScopeLinks
      };

      // Use the WASM function to perform the import directly
      const commands = await sched.perform_data_source_import(
        safeData,
        dataSource,
        tokenToUse,
        importOptions
      );

      setImportResults(commands);
    } catch (err) {
      console.error('Error importing data:', err);
      setError(`Failed to import data: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setImportLoading(false);
    }
  };

  // Render data source info
  const renderDataSourceInfo = () => {
    if (!dataSource) return null;

    let details = '';
    if (dataSource.configuration.type === 'GitHubRepository') {
      details = `${dataSource.configuration.organization}/${dataSource.configuration.repository}`;
    } else {
      details = dataSource.configuration.full_path;
    }

    return (
      <div className="mb-6 p-4 bg-cm-bg-elevated rounded-lg">
        <h2 className="text-xl font-semibold flex items-center mb-2">
          <FiDatabase className="mr-2" /> {dataSource.configuration.type}
        </h2>
        <p className="text-cm-text-primary">{details}</p>
      </div>
    );
  };

  const AcceptButton = () => {
    return (
      <div className="mb-3 w-full">
        <Button color="green" size="sm" className="w-full" onClick={handleAcceptImport} disabled={selectedCount === 0}>
          <FiCheck className="mr-2" /> Accept Selected ({selectedCount})
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <Button color="light" onClick={() => navigate(`/workspaces/${workspaceId}/data-sources`)}>
          <FiArrowLeft className="mr-2" /> Back to Data Sources
        </Button>
      </div>

      <h1 className="text-2xl font-bold text-cm-text-primary">Import Data</h1>

      {/* Error message */}
      {error && (
        <Alert color="failure">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Data source info */}
          {renderDataSourceInfo()}

          {/* Automatic Sync Button - Only show for GitLab data sources */}
          {dataSource && (dataSource.configuration.type === 'GitLabProject' || dataSource.configuration.type === 'GitLabGroup') && (
            <Card>
              <h2 className="text-xl font-semibold mb-4">Automatic Sync</h2>
              <p className="mb-4">
                Enable automatic synchronization to keep your data up-to-date whenever changes occur in GitLab.
                This will install a webhook in your GitLab {dataSource.configuration.type === 'GitLabProject' ? 'project' : 'group'}.
              </p>
              <Button
                color="purple"
                onClick={handleInstallWebhook}
                disabled={webhookLoading || !selectedCredentialId}
                className="flex items-center"
              >
                {webhookLoading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <FiRefreshCw className="mr-2" />
                    Turn on Automatic Sync
                  </>
                )}
              </Button>
              {!selectedCredentialId && (
                <div className="text-xs text-amber-500 mt-2">
                  Please select a credential above to enable automatic sync.
                </div>
              )}
            </Card>
          )}

          {/* Credential selection */}
          <Card>
            <h2 className="text-xl font-semibold mb-4">Authentication</h2>

            {credentialError && (
              <Alert color="failure" className="mb-4">
                {credentialError}
              </Alert>
            )}

            {credentialLoading ? (
              <div className="py-2">
                <LoadingSpinner size="sm" /> Loading credentials...
              </div>
            ) : (
              <div className="mb-4">
                <Label htmlFor="credentialSelect">Select Credential</Label>
                <Select
                  id="credentialSelect"
                  value={selectedCredentialId}
                  onChange={(e) => setSelectedCredentialId(e.target.value)}
                  className="mt-1"
                >
                  <option value="">Use default authentication</option>
                  {credentials.map((cred) => (
                    <option key={cred.id} value={cred.id}>{cred.id}</option>
                  ))}
                </Select>
                <div className="text-xs text-cm-text-muted mt-1">
                  Select a credential to use for this import, or use your current authentication.
                </div>
              </div>
            )}
          </Card>

          {/* Import form */}
          <Card>
            <h2 className="text-xl font-semibold mb-4">Import Settings</h2>

            <Formik
              initialValues={initialImportValues}
              validationSchema={importValidationSchema}
              onSubmit={handleImport}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form className="space-y-4">
                  <div>
                    <Label htmlFor="limit">Item Limit (Optional)</Label>
                    <TextInput
                      id="limit"
                      name="limit"
                      type="text"
                      placeholder="100"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setFieldValue('limit', value);
                        }
                      }}
                    />
                    {errors.limit && touched.limit && (
                      <div className="text-red-500 text-sm mt-1">{errors.limit}</div>
                    )}
                    <div className="text-xs text-cm-text-muted mt-1">
                      Maximum number of items to import. Leave empty for no limit.
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="updatedAfter">Updated After (Optional)</Label>
                    <Field name="updatedAfter">
                      {({ field, form }: { field: any; form: { setFieldValue: (field: string, value: any) => void } }) => (
                        <Datepicker
                          id="updatedAfter"
                          // Use the correct props for Flowbite Datepicker
                          value={field.value}
                          onChange={(date: Date | null) => form.setFieldValue('updatedAfter', date)}
                          showClearButton
                          showTodayButton
                          className="mt-1"
                        />
                      )}
                    </Field>
                    <div className="text-xs text-cm-text-muted mt-1">
                      Only import items updated after this date. Clear to import all items regardless of update date.
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Data Types to Import</Label>

                    <div className="flex items-center">
                      <Field
                        type="checkbox"
                        id="includePeople"
                        name="includePeople"
                        className="mr-2"
                      />
                      <Label htmlFor="includePeople" className="mb-0">People</Label>
                    </div>

                    <div className="flex items-center">
                      <Field
                        type="checkbox"
                        id="includeProjects"
                        name="includeProjects"
                        className="mr-2"
                      />
                      <Label htmlFor="includeProjects" className="mb-0">Projects</Label>
                    </div>

                    <div className="flex items-center">
                      <Field
                        type="checkbox"
                        id="includeWorkItems"
                        name="includeWorkItems"
                        className="mr-2"
                      />
                      <Label htmlFor="includeWorkItems" className="mb-0">Work Items</Label>
                    </div>

                    <div className="flex items-center">
                      <Field
                        type="checkbox"
                        id="followOutOfScopeLinks"
                        name="followOutOfScopeLinks"
                        className="mr-2"
                      />
                      <Label htmlFor="followOutOfScopeLinks" className="mb-0">Follow Out of Scope Links</Label>
                    </div>

                    {errors.includePeople && touched.includePeople && (
                      <div className="text-red-500 text-sm mt-1">{errors.includePeople}</div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    color="blue"
                    disabled={isSubmitting || importLoading}
                  >
                    {importLoading ? 'Importing...' : 'Import Data'}
                  </Button>
                </Form>
              )}
            </Formik>
          </Card>

          {importLoading && (
            <div className="mt-6">
              <LoadingSpinner message="Importing data..." />
            </div>
          )}

          {importResults && importResults.length > 0 && (
            <Card className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Import Results</h2>
                <Badge color="info">{importResults.length}</Badge>
              </div>

              <AcceptButton />

              <div className="overflow-x-auto rounded-md border border-cm-border-subtle">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-cm-bg-elevated">
                    <tr>
                      <th scope="col" className="px-3 py-2 w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => toggleAll(e.target.checked)}
                          ref={(el) => { if (el) el.indeterminate = someSelected; }}
                          aria-label="Select all"
                        />
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => toggleSort('type')}
                        aria-sort={sortBy === 'type' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        <span className="inline-flex items-center gap-1">
                          Type {sortBy === 'type' && (sortDir === 'asc' ? '▲' : '▼')}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => toggleSort('desc')}
                        aria-sort={sortBy === 'desc' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        <span className="inline-flex items-center gap-1">
                          Description {sortBy === 'desc' && (sortDir === 'asc' ? '▲' : '▼')}
                        </span>
                      </th>
                      <th scope="col" className="px-3 py-2 w-16 text-right text-xs font-medium uppercase tracking-wider">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="bg-cm-bg-surface divide-y divide-gray-200">
                    {(() => {
                      const rows = importResults.map((item, originalIndex) => {
                        const { kind, desc } = parseTitle(item.description.title);
                        return { item, originalIndex, kind, desc };
                      });
                      rows.sort((a, b) => {
                        const lhs = sortBy === 'type' ? a.kind : a.desc;
                        const rhs = sortBy === 'type' ? b.kind : b.desc;
                        const cmp = lhs.localeCompare(rhs);
                        return sortDir === 'asc' ? cmp : -cmp;
                      });
                      return rows.map(({ item, originalIndex, kind, desc }) => (
                        <tr key={originalIndex} className={!selectedRows[originalIndex] ? 'opacity-50' : ''}>
                          <td className="px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={!!selectedRows[originalIndex]}
                              onChange={() => toggleRow(originalIndex)}
                              aria-label={`Select row ${originalIndex + 1}`}
                            />
                          </td>
                          <td className="px-3 py-2 align-top whitespace-nowrap text-sm font-medium text-cm-text-primary">{kind}</td>
                          <td className="px-3 py-2 align-top text-sm text-cm-text-primary break-words">{desc}</td>
                          <td className="px-3 py-2 align-top text-right">
                            <Button color="light" size="xs" onClick={() => openDetail(originalIndex)} aria-label={`View details for row ${originalIndex + 1}`}>
                              <FiInfo />
                            </Button>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>

              <AcceptButton />
            </Card>
          )}

          {importResults && importResults.length === 0 && (
            <Alert color="info" className="mt-6">
              No data was imported. This could be because there was no new data to import or the selected options didn't match any available data.
            </Alert>
          )}

          {/* Detail Modal */}
          <Modal show={detailOpen} size="4xl" onClose={() => setDetailOpen(false)}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Command Detail</h3>
                <Button color="light" size="xs" onClick={() => setDetailOpen(false)}>Close</Button>
              </div>
              <div className="max-h-[70vh] overflow-auto">
                <pre className="text-xs whitespace-pre-wrap break-words bg-cm-bg-hover p-3 rounded">
                  {detailJson}
                </pre>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default DataSourceImportPage;
